
import { Component, Prop, Ref, Vue, Watch } from 'vue-property-decorator'
import EmailDialog from '@/components/Email/Dialog.vue'
import EmailForm from '@/components/Email/Form.vue'
import { Invoice } from '@/models/accounting/Invoice'
import InvoiceStatus from '@/enums/InvoiceStatus'
import Notification from '@/classes/Notification'
import User from '@/models/User'
import axios from 'axios'
import i18n from '@/i18n'

@Component({
	components: { EmailDialog, EmailForm }
})
export default class InvoiceEmailDialog extends Vue {
	@Prop({ required: true })
	show!: boolean

	@Prop({ required: true })
	invoiceId!: string

	@Ref('emailForm')
	emailFormRef!: EmailForm

	invoice: Invoice | null = null
	user: User | null = null
	isLoading: boolean = false
	isSending: boolean = false

	async created() {
		this.user = await User.fetchMeLazy()
		await this.loadInvoice()
	}

	@Watch('invoiceId')
	async onInvoiceIdChange() {
		await this.loadInvoice()
	}

	async loadInvoice() {
		if (!this.invoiceId) return
		this.isLoading = true
		try {
			const response = await Invoice.with(['customer']).find(this.invoiceId)
			const inv = response.getData() as Invoice
			// Pre-fill email fields
			inv.sendEmail = true
			inv.emailBody = this.defaultEmailBody(inv)
			inv.emailSubject = this.defaultEmailSubject(inv)
			inv.receiverEmails = inv.getCustomer()?.email ?? ''
			inv.emailFrom = this.user?.getCompany()?.name ?? ''
			inv.emailReplyTo = this.user?.emailInvoicing ?? ''
			this.invoice = inv
		} catch (err) {
            // eslint-disable-next-line
			console.error('Error loading invoice', err)
			await Notification.Error(i18n.t('errors.load_error').toString())
			this.$emit('close')
		} finally {
			this.isLoading = false
		}
	}

	defaultEmailBody(invoice: Invoice): string {
		return i18n.t('emailDialog.invoiceBody', {
			newline: '\n',
			firstName: invoice.getCustomer()?.name ?? '',
			senderFullName: this.user?.getCompany()?.name ?? '',
			senderSignature: this.user?.getCompany()?.emailSignature ?? ''
		}) as string
	}

	defaultEmailSubject(invoice: Invoice): string {
		return i18n.t('emailDialog.invoiceSubject', {
			invoiceNumber: invoice.fullInvoiceNumber ?? invoice.invoiceNumber ?? '',
			invoiceRecipient: this.user?.companyName ?? ''
		}) as string
	}

	async sendEmail() {
		if (!this.invoice) return
		const valid = await this.emailFormRef.$validator.validateAll()
		if (!valid) return

		this.isSending = true
		try {
			if (this.invoice.status !== InvoiceStatus.SENT) {
				await axios.post(
					`${this.invoice.getJsonApiBaseUrl()}${this.invoice.getJsonApiType()}/finalise`,
					// @ts-ignore - serialize is private in Coloquent but needed here (same pattern as Model.ts)
					this.invoice.serialize()
				)
				this.invoice.setStatus(InvoiceStatus.SENT)
			}

			await axios.post(
				`${this.invoice.getJsonApiBaseUrl()}${this.invoice.getJsonApiType()}/send`,
				// @ts-ignore - serialize is private in Coloquent but needed here (same pattern as Model.ts)
				this.invoice.serialize()
			)

			await Notification.Success(i18n.t('invoicing.table.notifications.emailSent.success').toString())
			this.$emit('sent', { invoiceId: this.invoice.getApiId() })
			this.$emit('close')
		} catch (err) {
            // eslint-disable-next-line
			console.error('Error sending invoice email', err)
			await Notification.Error(i18n.t('errors.email_send_error').toString())
		} finally {
			this.isSending = false
		}
	}
}
